import { AspectRatio, Flex, Image, motion } from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint';
import { assertUnreachable } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { BottomText } from 'src/modules/homeownerApp/components/BottomText';
import type { BottomTextProps } from 'src/modules/homeownerApp/components/BottomText';
import { preloadImage } from 'src/modules/homeownerApp/helpers';
import type { LandingPageStyle } from 'src/modules/pro/components/ImageGrid/SampleImage';
import { useProTranslate } from 'src/modules/pro/hooks';

import beforeBedroom from 'assets/img/homeownerApp/landingPageSamples/bedroom-before.jpg';
import coastalBedroom from 'assets/img/homeownerApp/landingPageSamples/bedroom-coastal.jpg';
import contemporaryBedroom from 'assets/img/homeownerApp/landingPageSamples/bedroom-contemporary.jpg';
import farmhouseBedroom from 'assets/img/homeownerApp/landingPageSamples/bedroom-farmhouse.jpg';
import midCenturyModernBedroom from 'assets/img/homeownerApp/landingPageSamples/bedroom-mid-century-modern.jpg';
import traditionalBedroom from 'assets/img/homeownerApp/landingPageSamples/bedroom-traditional.jpg';
import beforeExterior from 'assets/img/homeownerApp/landingPageSamples/exterior-before.jpg';
import coastalExterior from 'assets/img/homeownerApp/landingPageSamples/exterior-coastal.jpg';
import contemporaryExterior from 'assets/img/homeownerApp/landingPageSamples/exterior-contemporary.jpg';
import farmhouseExterior from 'assets/img/homeownerApp/landingPageSamples/exterior-farmhouse.jpg';
import midCenturyModernExterior from 'assets/img/homeownerApp/landingPageSamples/exterior-mid-century-modern.jpg';
import traditionalExterior from 'assets/img/homeownerApp/landingPageSamples/exterior-traditional.jpg';
import beforeKitchen from 'assets/img/homeownerApp/landingPageSamples/kitchen-before.jpg';
import coastalKitchen from 'assets/img/homeownerApp/landingPageSamples/kitchen-coastal.jpg';
import contemporaryKitchen from 'assets/img/homeownerApp/landingPageSamples/kitchen-contemporary.jpg';
import farmhouseKitchen from 'assets/img/homeownerApp/landingPageSamples/kitchen-farmhouse.jpg';
import midCenturyModernKitchen from 'assets/img/homeownerApp/landingPageSamples/kitchen-mid-century-modern.jpg';
import traditionalKitchen from 'assets/img/homeownerApp/landingPageSamples/kitchen-traditional.jpg';
import beforeLivingRoom from 'assets/img/homeownerApp/landingPageSamples/living-room-before.jpg';
import coastalLivingRoom from 'assets/img/homeownerApp/landingPageSamples/living-room-coastal.jpg';
import contemporaryLivingRoom from 'assets/img/homeownerApp/landingPageSamples/living-room-contemporary.jpg';
import farmhouseLivingRoom from 'assets/img/homeownerApp/landingPageSamples/living-room-farmhouse.jpg';
import midCenturyModernLivingRoom from 'assets/img/homeownerApp/landingPageSamples/living-room-mid-century-modern.jpg';
import traditionalLivingRoom from 'assets/img/homeownerApp/landingPageSamples/living-room-traditional.jpg';

type LandingPageSpaceType = 'Bedroom' | 'Exterior' | 'Kitchen' | 'Living Room';

export const styles = [
  'Contemporary',
  'Farmhouse',
  'Mid-Century Modern',
  'Traditional',
  'Coastal',
];
const allSampleImages = [
  beforeBedroom,
  coastalBedroom,
  contemporaryBedroom,
  farmhouseBedroom,
  midCenturyModernBedroom,
  traditionalBedroom,
  beforeExterior,
  coastalExterior,
  contemporaryExterior,
  farmhouseExterior,
  midCenturyModernExterior,
  traditionalExterior,
  beforeKitchen,
  coastalKitchen,
  contemporaryKitchen,
  farmhouseKitchen,
  midCenturyModernKitchen,
  traditionalKitchen,
  beforeLivingRoom,
  coastalLivingRoom,
  contemporaryLivingRoom,
  farmhouseLivingRoom,
  midCenturyModernLivingRoom,
  traditionalLivingRoom,
];

export const preloadSampleImages = () => {
  allSampleImages.forEach(image => preloadImage(image));
};

export const getBackgroundImage = ({
  style,
  spaceType,
  isBeforeSelected = false,
}: {
  style?: LandingPageStyle;
  spaceType: LandingPageSpaceType;
  isBeforeSelected: boolean;
}) => {
  switch (spaceType) {
    case 'Exterior':
      if (isBeforeSelected) {
        return beforeExterior;
      }
      if (style === 'Coastal') {
        return coastalExterior;
      }
      if (style === 'Contemporary') {
        return contemporaryExterior;
      }
      if (style === 'Farmhouse') {
        return farmhouseExterior;
      }
      if (style === 'Mid-Century Modern') {
        return midCenturyModernExterior;
      }
      return traditionalExterior;
    case 'Bedroom':
      if (isBeforeSelected) {
        return beforeBedroom;
      }
      if (style === 'Coastal') {
        return coastalBedroom;
      }
      if (style === 'Contemporary') {
        return contemporaryBedroom;
      }
      if (style === 'Farmhouse') {
        return farmhouseBedroom;
      }
      if (style === 'Mid-Century Modern') {
        return midCenturyModernBedroom;
      }
      return traditionalBedroom;
    case 'Kitchen':
      if (isBeforeSelected) {
        return beforeKitchen;
      }
      if (style === 'Coastal') {
        return coastalKitchen;
      }
      if (style === 'Contemporary') {
        return contemporaryKitchen;
      }
      if (style === 'Farmhouse') {
        return farmhouseKitchen;
      }
      if (style === 'Mid-Century Modern') {
        return midCenturyModernKitchen;
      }
      return traditionalKitchen;
    case 'Living Room':
      if (isBeforeSelected) {
        return beforeLivingRoom;
      }
      if (style === 'Coastal') {
        return coastalLivingRoom;
      }
      if (style === 'Contemporary') {
        return contemporaryLivingRoom;
      }
      if (style === 'Farmhouse') {
        return farmhouseLivingRoom;
      }
      if (style === 'Mid-Century Modern') {
        return midCenturyModernLivingRoom;
      }
      return traditionalLivingRoom;
    default:
      assertUnreachable(spaceType);
  }
  return '';
};

const MotionImage = motion(Image);

interface ExampleCardProps extends FlexProps {
  spaceType: LandingPageSpaceType;
  styleName: LandingPageStyle;
  isBeforeSelected: boolean;
  maxImageHeight?: string;
  bottomTextProps?: BottomTextProps;
  ratio?: number;
}

const SampleImage = ({
  bottomTextProps,
  spaceType,
  styleName,
  isBeforeSelected = true,
  maxImageHeight,
  ratio,
  ...rest
}: ExampleCardProps) => {
  const t = useProTranslate();
  const sampleImageUrl = getBackgroundImage({
    style: styleName,
    isBeforeSelected,
    spaceType,
  });

  const imageDescription = `${
    isBeforeSelected ? '' : 'Before: '
  }${styleName}, ${spaceType}`;

  return (
    <Flex alignItems="flex-start" flexDirection="column" width="100%" {...rest}>
      <AspectRatio
        height={ratio ? undefined : '100%'}
        maxHeight={maxImageHeight}
        ratio={ratio}
        width="100%"
      >
        <AnimatePresence initial={false} mode="popLayout">
          <MotionImage
            alt={imageDescription}
            animate={{ opacity: 1 }}
            borderRadius={350}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            key={imageDescription}
            src={sampleImageUrl}
          />
        </AnimatePresence>
      </AspectRatio>
      <BottomText
        ml={200}
        mt={400}
        primaryText={spaceType}
        secondaryText={isBeforeSelected ? t('before') : styleName}
        {...bottomTextProps}
      />
    </Flex>
  );
};

export { SampleImage };
